import { useEffect } from "react";

const mobileSeo = {
  title: "Jet Black Limousine | NYC & Westchester Airport Car Service",
  description: "Jet Black Limousine provides NYC and Westchester airport car service, black car service, corporate transportation and private chauffeur service to JFK, LGA, EWR, HPN and SWF. Call (914) 962-8444.",
  canonical: "https://www.jetblackairport-luxury.com/",
  robots: "index, follow",
  image: "https://www.jetblackairport-luxury.com/favicon.svg",
};

const getOrCreateHeadElement = ({ selector, tagName, attributes }) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement(tagName);
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
    document.head.appendChild(element);
  }

  return element;
};

const mobileJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      "@id": "https://www.jetblackairport-luxury.com/#localbusiness",
      name: "Jet Black Limousine",
      alternateName: "Jet Black Luxury Car Service",
      description: "Airport car service, black car service, corporate transportation and private chauffeur service serving NYC, Westchester County, JFK, LGA, EWR, HPN and SWF.",
      url: "https://www.jetblackairport-luxury.com/",
      telephone: "+19149628444",
      priceRange: "$$",
      image: "https://www.jetblackairport-luxury.com/favicon.svg",
      address: {
        "@type": "PostalAddress",
        streetAddress: "395 Hallocks Mill Rd",
        addressLocality: "Yorktown Heights",
        addressRegion: "NY",
        postalCode: "10598",
        addressCountry: "US",
      },
      areaServed: [
        "New York City",
        "Westchester County",
        "JFK Airport",
        "LaGuardia Airport",
        "Newark Liberty International Airport",
        "Westchester County Airport",
        "Stewart International Airport",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+19149628444",
        contactType: "reservations",
        areaServed: "US",
      },
    },
    {
      "@type": "Organization",
      "@id": "https://www.jetblackairport-luxury.com/#organization",
      name: "Jet Black Limousine",
      url: "https://www.jetblackairport-luxury.com/",
      telephone: "+19149628444",
      logo: "https://www.jetblackairport-luxury.com/favicon.svg",
    },
    {
      "@type": "WebSite",
      "@id": "https://www.jetblackairport-luxury.com/#website",
      name: "Jet Black Limousine",
      url: "https://www.jetblackairport-luxury.com/",
      publisher: { "@id": "https://www.jetblackairport-luxury.com/#organization" },
    },
    {
      "@type": "Service",
      "@id": "https://www.jetblackairport-luxury.com/#mobile-public-service",
      name: "NYC and Westchester Airport Car Service",
      provider: { "@id": "https://www.jetblackairport-luxury.com/#localbusiness" },
      serviceType: [
        "airport car service",
        "black car service",
        "corporate transportation",
        "private chauffeur service",
      ],
      areaServed: ["NYC", "Westchester County", "JFK", "LGA", "EWR", "HPN", "SWF"],
      url: "https://www.jetblackairport-luxury.com/",
    },
  ],
};

function MobilePublicSeo() {
  useEffect(() => {
    const isAppPath = window.location.pathname === "/app" || window.location.pathname.startsWith("/app/");
    const currentCanonical = `https://www.jetblackairport-luxury.com${window.location.pathname}`;
    const previousTitle = document.title;
    document.title = mobileSeo.title;

    const description = getOrCreateHeadElement({
      selector: 'meta[name="description"]',
      tagName: "meta",
      attributes: { name: "description" },
    });
    const previousDescription = description.getAttribute("content");
    description.setAttribute("content", mobileSeo.description);

    const hadRobots = Boolean(document.head.querySelector('meta[name="robots"]'));
    const robots = getOrCreateHeadElement({
      selector: 'meta[name="robots"]',
      tagName: "meta",
      attributes: { name: "robots" },
    });
    const previousRobots = robots.getAttribute("content");
    robots.setAttribute("content", isAppPath ? "noindex, follow" : mobileSeo.robots);

    const hadCanonical = Boolean(document.head.querySelector('link[rel="canonical"]'));
    const canonical = getOrCreateHeadElement({
      selector: 'link[rel="canonical"]',
      tagName: "link",
      attributes: { rel: "canonical" },
    });
    const previousCanonical = canonical.getAttribute("href");
    canonical.setAttribute("href", isAppPath ? currentCanonical : mobileSeo.canonical);

    const socialTags = [
      { selector: 'meta[property="og:title"]', tagName: "meta", attributes: { property: "og:title" }, content: mobileSeo.title },
      { selector: 'meta[property="og:description"]', tagName: "meta", attributes: { property: "og:description" }, content: mobileSeo.description },
      { selector: 'meta[property="og:url"]', tagName: "meta", attributes: { property: "og:url" }, content: isAppPath ? currentCanonical : mobileSeo.canonical },
      { selector: 'meta[property="og:type"]', tagName: "meta", attributes: { property: "og:type" }, content: "website" },
      { selector: 'meta[property="og:image"]', tagName: "meta", attributes: { property: "og:image" }, content: mobileSeo.image },
      { selector: 'meta[name="twitter:card"]', tagName: "meta", attributes: { name: "twitter:card" }, content: "summary_large_image" },
      { selector: 'meta[name="twitter:title"]', tagName: "meta", attributes: { name: "twitter:title" }, content: mobileSeo.title },
      { selector: 'meta[name="twitter:description"]', tagName: "meta", attributes: { name: "twitter:description" }, content: mobileSeo.description },
      { selector: 'meta[name="twitter:image"]', tagName: "meta", attributes: { name: "twitter:image" }, content: mobileSeo.image },
    ];

    const previousSocialTags = socialTags.map((tag) => {
      const hadElement = Boolean(document.head.querySelector(tag.selector));
      const element = getOrCreateHeadElement(tag);
      const previousContent = element.getAttribute("content");
      element.setAttribute("content", tag.content);
      return { element, hadElement, previousContent };
    });

    const jsonLd = getOrCreateHeadElement({
      selector: 'script[data-mobile-public-json-ld="true"]',
      tagName: "script",
      attributes: { type: "application/ld+json", "data-mobile-public-json-ld": "true" },
    });
    jsonLd.textContent = JSON.stringify(mobileJsonLd);

    return () => {
      document.title = previousTitle;
      if (previousDescription) description.setAttribute("content", previousDescription);
      if (previousRobots) {
        robots.setAttribute("content", previousRobots);
      } else if (!hadRobots) {
        robots.remove();
      }
      if (previousCanonical) {
        canonical.setAttribute("href", previousCanonical);
      } else if (!hadCanonical) {
        canonical.remove();
      }
      previousSocialTags.forEach(({ element, hadElement, previousContent }) => {
        if (previousContent) {
          element.setAttribute("content", previousContent);
        } else if (!hadElement) {
          element.remove();
        }
      });
      jsonLd.remove();
    };
  }, []);

  return null;
}

export default MobilePublicSeo;
